import type { Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { number, object, safeParse, string } from "valibot";
import { dataName, gridFSFile, label, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { EXISTS_CONDITION_KEYS } from "../../constants";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import FileDownloadButton from "../valueRendering/FileDownloadButton";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector } from "i18next";

const DtoSchema = object({
  label_text: string(),
  data_name: string(),
  required: required(),
  max_duration: number(),
  quality: string(),
});

const DomainSchema = object({
  label_text: label(),
  data_name: dataName(),
  required: required(),
});

const ValueSchema = gridFSFile();

export type VideoFieldProperties = InferOutput<typeof DomainSchema>;
type VideoFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const VideoField: Field<VideoFieldProperties, VideoFieldDtoType, ValueType> = {
  type: "video",
  category: "media",
  widget: "com.moreapps.plugins:video:6",
  schema: DomainSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    required: false,
  }),
  ruleBehaviour: {
    conditions: { keys: EXISTS_CONDITION_KEYS, variant: "none" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  stringValue: (value) => value,
  renderValue: (value) => <FileDownloadButton fileId={value} />,
  icon: "videoCamera",
  titleKey: keyFromSelector(($) => $.VIDEO_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.VIDEO_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
  },
  toDto: (domain) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      max_duration: 60, // '60' is the default in the old widget definition - To be removed with DEV-4757
      quality: "1", // '1' ("High") is the default in the old widget definition - To be removed with DEV-4757
    });
    return output as VideoFieldDtoType;
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <FieldButtonPreview icon="paperClip" text={t(($) => $.VIDEO_FIELD_PREVIEW_ADD_BUTTON_LABEL)} />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(VideoField);
